import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { workOrdersApi, companiesApi, studiesApi, priceListsApi, studyPricesApi, doctorsApi, profilesApi, branchesApi } from "@/lib/api";
import { toast } from "sonner";
import { 
  FileEdit, Search, Hash, User, ChevronLeft, ChevronRight, 
  Trash2, Download, Printer, X, Receipt, Tag, Eye, Check, ChevronsUpDown, CreditCard, Package, Zap, Loader2
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import MobilePDFViewer from "@/components/MobilePDFViewer";
import { printRecibo, printEtiquetas, printTicket58 } from "@/lib/qzTray";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export default function EditRequest() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [folioSearch, setFolioSearch] = useState("");
  const [patientSearch, setPatientSearch] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editData, setEditData] = useState({ 
    notes: "", 
    discount: 0, 
    company_id: "",
    doctor_id: "",
    studies: [],
    branch: ""
  });
  const [saving, setSaving] = useState(false);
  const [firstFolioOfDay, setFirstFolioOfDay] = useState("");
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [branches, setBranches] = useState([]);
  
  // PDF Preview states
  const [showReceiptPreview, setShowReceiptPreview] = useState(false);
  const [showLabelPreview, setShowLabelPreview] = useState(false);
  const [showTicketPreview, setShowTicketPreview] = useState(false);
  const [receiptUrl, setReceiptUrl] = useState("");
  const [labelUrl, setLabelUrl] = useState("");
  const [ticketUrl, setTicketUrl] = useState("");
  
  // Payment dialog states
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentData, setPaymentData] = useState({
    additionalAmount: "",
    newTotal: 0,
    previousTotal: 0,
    previousPaid: 0,
    difference: 0,
  });
  const [pendingOrderUpdate, setPendingOrderUpdate] = useState(null);
  
  // Catalogs
  const [companies, setCompanies] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [availableStudies, setAvailableStudies] = useState([]);
  const [availableProfiles, setAvailableProfiles] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [studyPrices, setStudyPrices] = useState([]); // Prices for current price list
  const [activePriceListId, setActivePriceListId] = useState("");
  
  // Doctor search
  const [doctorSearchOpen, setDoctorSearchOpen] = useState(false);
  const [doctorSearchTerm, setDoctorSearchTerm] = useState("");
  
  // Study search (now includes profiles)
  const [studySearchTerm, setStudySearchTerm] = useState("");
  const [showStudyDropdown, setShowStudyDropdown] = useState(false);
  const [studyDropdownIndex, setStudyDropdownIndex] = useState(0);
  const studySearchRef = useRef(null);

  // Fetch data on mount
  useEffect(() => {
    fetchOrders();
    fetchCatalogs();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (studySearchRef.current && !studySearchRef.current.contains(event.target)) {
        setShowStudyDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchCatalogs = async () => {
    try {
      const [companiesRes, doctorsRes, studiesRes, priceListsRes, branchesRes] = await Promise.all([
        companiesApi.getAll(),
        doctorsApi.getAll(),
        studiesApi.getAll(),
        priceListsApi.getAll(),
        branchesApi.getAll()
      ]);
      setCompanies(companiesRes.data);
      setDoctors(doctorsRes.data.filter(d => d.active !== false));
      setAvailableStudies(studiesRes.data);
      setPriceLists(priceListsRes.data);
      setBranches(branchesRes.data);
      
      // Set default price list
      const defaultList = priceListsRes.data.find(p => p.is_default);
      if (defaultList) {
        setActivePriceListId(defaultList.id);
        await fetchStudyPrices(defaultList.id);
        await fetchProfilesWithPrices(defaultList.id);
      }
    } catch (error) {
      console.error("Error fetching catalogs:", error);
    }
  };

  const fetchStudyPrices = async (priceListId) => {
    if (!priceListId) return;
    try {
      const response = await studyPricesApi.getAll(priceListId);
      setStudyPrices(response.data);
    } catch (error) {
      console.error("Error fetching study prices:", error);
    }
  };
  
  const fetchProfilesWithPrices = async (priceListId) => {
    if (!priceListId) return;
    try {
      const response = await profilesApi.getWithPrices(priceListId);
      setAvailableProfiles(response.data);
    } catch (error) {
      console.error("Error fetching profiles:", error);
    }
  };

  // When company changes, find matching price list
  const handleCompanyChange = async (companyId) => {
    setEditData(prev => ({ ...prev, company_id: companyId === "none" ? "" : companyId }));
    
    if (companyId && companyId !== "none") {
      const company = companies.find(c => c.id === companyId);
      if (company) {
        // Find price list with same name as company
        const matchingPriceList = priceLists.find(
          p => p.name.toLowerCase() === company.name.toLowerCase()
        );
        if (matchingPriceList) {
          setActivePriceListId(matchingPriceList.id);
          await fetchStudyPrices(matchingPriceList.id);
          await fetchProfilesWithPrices(matchingPriceList.id);
          toast.info(`Lista de precios: ${matchingPriceList.name}`);
        }
      }
    } else {
      // Use default price list
      const defaultList = priceLists.find(p => p.is_default);
      if (defaultList) {
        setActivePriceListId(defaultList.id);
        await fetchStudyPrices(defaultList.id);
        await fetchProfilesWithPrices(defaultList.id);
      }
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await workOrdersApi.getAll();
      const allOrders = response.data;
      setOrders(allOrders);
      
      // Generate today's folio prefix (YYMMDD format)
      const today = new Date();
      const yy = today.getFullYear().toString().slice(-2);
      const mm = (today.getMonth() + 1).toString().padStart(2, '0');
      const dd = today.getDate().toString().padStart(2, '0');
      const todayFolioBase = `${yy}${mm}${dd}000`; // e.g., "260116000"
      
      setFirstFolioOfDay(todayFolioBase);
      setFolioSearch(todayFolioBase); // Set as default search value
      
    } catch (error) {
      toast.error("Error al cargar solicitudes");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (order) => {
    setSelectedOrder(order);
    setEditData({
      notes: order.notes || "",
      discount: order.discount || 0,
      company_id: order.company_id || "",
      doctor_id: order.doctor_id || "",
      studies: order.studies || [],
      branch: order.branch || ""
    });
    
    // Set price list based on company
    if (order.company_id) {
      const company = companies.find(c => c.id === order.company_id);
      if (company) {
        const matchingPriceList = priceLists.find(
          p => p.name.toLowerCase() === company.name.toLowerCase()
        );
        if (matchingPriceList) {
          setActivePriceListId(matchingPriceList.id);
          await fetchStudyPrices(matchingPriceList.id);
        }
      }
    } else if (order.price_list_id) {
      setActivePriceListId(order.price_list_id);
      await fetchStudyPrices(order.price_list_id);
    }
  };

  // Get price for a study from current price list
  const getStudyPrice = (studyId) => {
    const priceEntry = studyPrices.find(p => p.study_id === studyId);
    if (priceEntry) return priceEntry.price;
    
    // Fallback to base price
    const study = availableStudies.find(s => s.id === studyId);
    return study?.base_price || 0;
  };

  // Calculate totals
  const calculateTotals = () => {
    const subtotal = editData.studies.reduce((sum, s) => sum + (s.price || 0), 0);
    const total = subtotal - (editData.discount || 0);
    return { subtotal, total: Math.max(0, total) };
  };

  // Filter studies for search
  const filteredStudies = availableStudies.filter(study => {
    if (!studySearchTerm) return false;
    const searchLower = studySearchTerm.toLowerCase();
    const nameMatch = study.name?.toLowerCase().includes(searchLower);
    const codeMatch = study.code?.toLowerCase().includes(searchLower);
    const alreadyAdded = editData.studies.some(s => s.study_id === study.id);
    return (nameMatch || codeMatch) && !alreadyAdded;
  });
  
  // Filter profiles for search
  const filteredProfiles = availableProfiles.filter(profile => {
    if (!studySearchTerm) return false;
    const searchLower = studySearchTerm.toLowerCase();
    const nameMatch = profile.name?.toLowerCase().includes(searchLower);
    const codeMatch = profile.code?.toLowerCase().includes(searchLower);
    // Check if profile is already added (by profile_id)
    const alreadyAdded = editData.studies.some(s => s.profile_id === profile.id);
    return (nameMatch || codeMatch) && !alreadyAdded;
  });
  
  // Combined search results (profiles first, then studies)
  const combinedSearchResults = [
    ...filteredProfiles.map(p => ({ ...p, type: 'profile' })),
    ...filteredStudies.map(s => ({ ...s, type: 'study' }))
  ];

  // Filter doctors for search
  const filteredDoctors = doctors.filter((doctor) =>
    doctor.name?.toLowerCase().includes(doctorSearchTerm.toLowerCase())
  );

  // Get selected doctor name
  const getSelectedDoctorName = () => {
    if (!editData.doctor_id) return "A QUIEN CORRESPONDA";
    const doctor = doctors.find(d => d.id === editData.doctor_id);
    return doctor?.name || "A QUIEN CORRESPONDA";
  };

  // Add study to order
  const addStudy = (study) => {
    const price = getStudyPrice(study.id);
    
    const newStudy = {
      study_id: study.id,
      study_name: study.name,
      study_code: study.code || "",
      price: price
    };
    
    setEditData(prev => ({
      ...prev,
      studies: [...prev.studies, newStudy]
    }));
    
    setStudySearchTerm("");
    setShowStudyDropdown(false);
    toast.success(`${study.name} agregado - $${price}`);
  };
  
  // Add profile (expands to include all studies within)
  const addProfile = (profile) => {
    if (!profile.study_ids || profile.study_ids.length === 0) {
      toast.error("Este perfil no tiene estudios configurados");
      return;
    }
    
    // Get all studies that belong to this profile
    const profileStudies = [];
    let addedCount = 0;
    
    for (const studyId of profile.study_ids) {
      // Check if this study is already in the order
      const alreadyAdded = editData.studies.some(s => s.study_id === studyId);
      if (alreadyAdded) continue;
      
      const study = availableStudies.find(s => s.id === studyId);
      if (study) {
        profileStudies.push({
          study_id: study.id,
          study_name: study.name,
          study_code: study.code || "",
          price: 0, // Individual study price is 0, profile price is used
          from_profile: profile.name
        });
        addedCount++;
      }
    }
    
    // Add the profile itself as a line item for pricing
    const profileLineItem = {
      study_id: `profile_${profile.id}`,
      study_name: `📦 ${profile.name}`,
      study_code: profile.code || "",
      price: profile.price || 0,
      is_profile: true,
      profile_id: profile.id
    };
    
    setEditData(prev => ({
      ...prev,
      studies: [...prev.studies, profileLineItem, ...profileStudies]
    }));
    
    setStudySearchTerm("");
    setShowStudyDropdown(false);
    toast.success(`Perfil ${profile.name} agregado (${addedCount} estudios) - $${profile.price}`);
  };

  // Remove study from order
  const removeStudy = (studyId) => {
    const study = editData.studies.find(s => s.study_id === studyId);
    
    // If removing a profile, also remove its studies
    if (study?.is_profile && study?.profile_id) {
      const profileStudies = editData.studies.filter(s => s.from_profile === study.study_name.replace('📦 ', ''));
      setEditData(prev => ({
        ...prev,
        studies: prev.studies.filter(s => 
          s.study_id !== studyId && 
          s.from_profile !== study.study_name.replace('📦 ', '')
        )
      }));
      toast.info(`Perfil ${study.study_name} y sus estudios eliminados`);
    } else {
      setEditData(prev => ({
        ...prev,
        studies: prev.studies.filter(s => s.study_id !== studyId)
      }));
      if (study) {
        toast.info(`${study.study_name} eliminado - $${study.price}`);
      }
    }
  };

  // Check if total changed and open payment dialog
  const handleSave = async () => {
    if (!selectedOrder) return;
    
    const { subtotal, total } = calculateTotals();
    const previousTotal = selectedOrder.total || 0;
    const previousPaid = selectedOrder.amount_paid || 0;
    const difference = total - previousTotal;
    const pendingBalance = Math.max(0, total - previousPaid);
    
    const company = companies.find(c => c.id === editData.company_id);
    const doctor = doctors.find(d => d.id === editData.doctor_id);
    
    // Store the pending update data
    setPendingOrderUpdate({
      ...selectedOrder,
      notes: editData.notes,
      discount: editData.discount,
      company_id: editData.company_id,
      company_name: company?.name || null,
      doctor_id: editData.doctor_id,
      doctor_name: doctor?.name || null,
      studies: editData.studies,
      subtotal: subtotal,
      total: total,
      price_list_id: activePriceListId,
      branch: editData.branch || null
    });
    
    // Show payment dialog if there's pending balance or total increased
    if (pendingBalance > 0 || difference > 0) {
      setPaymentData({
        additionalAmount: "",
        newTotal: total,
        previousTotal: previousTotal,
        previousPaid: previousPaid,
        difference: difference,
        pendingBalance: pendingBalance,
      });
      
      setShowPaymentDialog(true);
    } else {
      // No pending balance - save directly
      await saveOrderDirectly();
    }
  };
  
  // Save order without payment dialog (when total didn't increase)
  const saveOrderDirectly = async () => {
    if (!selectedOrder) return;
    setSaving(true);

    try {
      const { subtotal, total } = calculateTotals();
      const company = companies.find(c => c.id === editData.company_id);
      const doctor = doctors.find(d => d.id === editData.doctor_id);
      
      // Recalculate pending balance if total changed
      const previousPaid = selectedOrder.amount_paid || 0;
      const newPendingBalance = Math.max(0, total - previousPaid);
      
      await workOrdersApi.update(selectedOrder.id, {
        ...selectedOrder,
        notes: editData.notes,
        discount: editData.discount,
        company_id: editData.company_id,
        company_name: company?.name || null,
        doctor_id: editData.doctor_id,
        doctor_name: doctor?.name || null,
        studies: editData.studies,
        subtotal: subtotal,
        total: total,
        pending_balance: newPendingBalance,
        price_list_id: activePriceListId,
        branch: editData.branch || null
      });

      toast.success("Solicitud actualizada correctamente");
      fetchOrders();
    } catch (error) {
      toast.error("Error al actualizar solicitud");
    } finally {
      setSaving(false);
    }
  };
  
  // Handle payment confirmation
  const handlePaymentConfirm = async () => {
    if (!pendingOrderUpdate) return;
    setSaving(true);
    
    try {
      const additionalPaid = parseFloat(paymentData.additionalAmount) || 0;
      const totalPaid = (paymentData.previousPaid || 0) + additionalPaid;
      const newPendingBalance = Math.max(0, paymentData.newTotal - totalPaid);
      
      await workOrdersApi.update(selectedOrder.id, {
        ...pendingOrderUpdate,
        amount_paid: totalPaid,
        pending_balance: newPendingBalance
      });
      
      setShowPaymentDialog(false);
      setPendingOrderUpdate(null);
      
      if (additionalPaid > 0) {
        toast.success(`Solicitud actualizada. Pago adicional: $${additionalPaid.toLocaleString()}`);
      } else {
        toast.success("Solicitud actualizada correctamente");
      }
      
      fetchOrders();
    } catch (error) {
      toast.error("Error al actualizar solicitud");
    } finally {
      setSaving(false);
    }
  };
  
  // Calculate new pending balance for display
  const calculateNewPendingBalance = () => {
    const additionalPaid = parseFloat(paymentData.additionalAmount) || 0;
    const totalPaid = (paymentData.previousPaid || 0) + additionalPaid;
    return Math.max(0, paymentData.newTotal - totalPaid);
  };

  // Print/Download receipt PDF
  const handleReceipt = async (action) => {
    if (!selectedOrder) return;
    try {
      const response = await workOrdersApi.downloadReceipt(selectedOrder.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      if (action === 'preview') {
        setReceiptUrl(url);
        setShowReceiptPreview(true);
      } else if (action === 'direct') {
        toast.info("Enviando a impresora...");
        const result = await printRecibo(url);
        if (result.success) {
          toast.success(result.message);
        } else {
          toast.error(result.message);
        }
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      } else if (action === 'print') {
        const printWindow = window.open(url, '_blank');
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print();
          };
        }
        toast.success("Abriendo recibo para imprimir...");
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.download = `recibo_${selectedOrder.order_number || selectedOrder.id}.pdf`;
        link.click();
        toast.success("Recibo descargado");
      }
      
      if (action !== 'preview') {
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      }
    } catch (error) {
      toast.error("Error al generar recibo");
    }
  };

  // Print ticket 58mm (impresora térmica) - mismos datos que el recibo
  const handleTicket58 = async (action) => {
    if (!selectedOrder) return;
    try {
      const response = await workOrdersApi.downloadReceiptTicket(selectedOrder.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      if (action === 'preview') {
        setTicketUrl(url);
        setShowTicketPreview(true);
      } else if (action === 'direct') {
        toast.info("Enviando ticket a impresora 58mm...");
        const result = await printTicket58(url);
        if (result.success) {
          toast.success(result.message);
        } else {
          toast.error(result.message);
        }
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      } else if (action === 'print') {
        const printWindow = window.open(url, '_blank');
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print();
          };
        }
        toast.success("Abriendo ticket para imprimir...");
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.download = `ticket_${selectedOrder.order_number || selectedOrder.id}.pdf`;
        link.click();
        toast.success("Ticket descargado");
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      }
    } catch (error) {
      toast.error("Error al generar ticket 58mm");
    }
  };

  // Print/Download labels PDF
  const handleLabels = async (action) => {
    if (!selectedOrder) return;
    try {
      const response = await workOrdersApi.downloadLabel(selectedOrder.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      if (action === 'preview') {
        setLabelUrl(url);
        setShowLabelPreview(true);
      } else if (action === 'direct') {
        toast.info("Enviando a impresora...");
        const result = await printEtiquetas(url);
        if (result.success) {
          toast.success(result.message);
        } else {
          toast.error(result.message);
        }
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      } else if (action === 'print') {
        const printWindow = window.open(url, '_blank');
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print();
          };
        }
        toast.success("Abriendo etiquetas para imprimir...");
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.download = `etiquetas_${selectedOrder.order_number || selectedOrder.id}.pdf`;
        link.click();
        toast.success("Etiquetas descargadas");
      }
      
      if (action !== 'preview') {
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      }
    } catch (error) {
      toast.error("Error al generar etiquetas");
    }
  };

  // Delete order
  const handleDeleteOrder = async (order, event) => {
    if (event) {
      event.stopPropagation();
    }
    
    const confirmed = window.confirm(
      `¿Está seguro de eliminar la solicitud ${order.order_number || order.patient_folio}?\n\nPaciente: ${order.patient_name}\n\nEsta acción no se puede deshacer.`
    );
    
    if (!confirmed) return;
    
    try {
      await workOrdersApi.delete(order.id);
      toast.success("Solicitud eliminada exitosamente");
      
      // Refresh orders list
      await fetchOrders();
      
      // Clear selection if deleted order was selected
      if (selectedOrder?.id === order.id) {
        setSelectedOrder(null);
      }
    } catch (error) {
      toast.error("Error al eliminar la solicitud");
      console.error(error);
    }
  };

  // Filter orders
  const filteredOrders = orders.filter((order) => {
    const folioMatch = folioSearch === "" || 
      (order.order_number?.toLowerCase().includes(folioSearch.toLowerCase()) ||
       order.patient_folio?.toLowerCase().includes(folioSearch.toLowerCase()));
    
    const patientMatch = patientSearch === "" ||
      order.patient_name?.toLowerCase().includes(patientSearch.toLowerCase());
    
    return folioMatch && patientMatch;
  });

  // Keyboard navigation for orders - must be after filteredOrders is defined
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle when no order is selected (dialog closed)
      if (selectedOrder) return;
      
      // Skip if user is typing in an input or textarea
      const activeEl = document.activeElement;
      if (activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA')) return;
      
      if (filteredOrders.length === 0) return;
      
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setCurrentIndex(prev => {
          const newIndex = prev < filteredOrders.length - 1 ? prev + 1 : 0;
          return newIndex;
        });
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setCurrentIndex(prev => {
          const newIndex = prev > 0 ? prev - 1 : filteredOrders.length - 1;
          return newIndex;
        });
      } else if (event.key === 'Enter') {
        event.preventDefault();
        const idx = currentIndex >= 0 ? currentIndex : 0;
        const order = filteredOrders[idx];
        if (order) {
          setSelectedOrder(order);
          setEditData({
            notes: order.notes || "",
            discount: order.discount || 0,
            company_id: order.company_id || "",
            studies: order.studies || []
          });
        }
      }
    };
    
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [currentIndex, filteredOrders, selectedOrder]);

  // Reset index when orders change or search changes
  useEffect(() => {
    if (filteredOrders.length > 0) {
      setCurrentIndex(0);
    } else {
      setCurrentIndex(-1);
    }
  }, [filteredOrders.length, folioSearch, patientSearch]);

  // Navigation functions
  const goToPrevious = () => {
    if (filteredOrders.length === 0) return;
    let newIndex = currentIndex <= 0 ? filteredOrders.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    handleEdit(filteredOrders[newIndex]);
  };

  const goToNext = () => {
    if (filteredOrders.length === 0) return;
    let newIndex = currentIndex >= filteredOrders.length - 1 || currentIndex < 0 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    handleEdit(filteredOrders[newIndex]);
  };

  const handleSelectOrder = (order, index) => {
    setCurrentIndex(index);
    handleEdit(order);
  };

  // Format helpers
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const { subtotal, total } = calculateTotals();

  return (
    <div className="space-y-6" data-testid="edit-request-page">
      <div>
        <h1 className="text-2xl font-bold font-['Manrope'] tracking-tight">
          Editar Solicitud
        </h1>
        <p className="text-muted-foreground">
          Modifique órdenes de trabajo por folio o nombre de paciente
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileEdit className="h-5 w-5" />
              Búsqueda de Solicitudes
            </CardTitle>
            
            {/* Search fields row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Folio search */}
              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <Hash className="h-4 w-4" />
                  Folio
                </Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por folio..."
                    value={folioSearch}
                    onChange={(e) => setFolioSearch(e.target.value)}
                    className="pl-10 h-10"
                    data-testid="search-folio"
                  />
                </div>
              </div>

              {/* Patient search */}
              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Paciente
                </Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por nombre de paciente..."
                    value={patientSearch}
                    onChange={(e) => setPatientSearch(e.target.value)}
                    className="pl-10 h-10"
                    data-testid="search-patient"
                  />
                </div>
              </div>
            </div>

            {/* Results count - only show when searching */}
            {(folioSearch || patientSearch) && (
              <div className="text-sm text-muted-foreground flex items-center justify-between">
                <span>{filteredOrders.length} solicitud(es) encontrada(s)</span>
                {currentIndex >= 0 && selectedOrder && (
                  <span className="font-medium">
                    Posición: {currentIndex + 1} de {filteredOrders.length}
                  </span>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        
        {/* Only show content when there's a search term */}
        {(folioSearch || patientSearch) && (
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Cargando...</div>
            ) : filteredOrders.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No se encontraron solicitudes con los criterios de búsqueda
              </div>
            ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Folio</TableHead>
                    <TableHead>Paciente</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Hora</TableHead>
                    <TableHead>Estudios</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.map((order, index) => (
                    <TableRow 
                      key={order.id}
                      className={`cursor-pointer hover:bg-muted/50 transition-colors ${
                        currentIndex === index ? "bg-primary/20 border-l-4 border-l-primary" : ""
                      }`}
                      onClick={() => handleSelectOrder(order, index)}
                    >
                      <TableCell className="font-medium font-mono">
                        {order.order_number || order.patient_folio || "-"}
                      </TableCell>
                      <TableCell>{order.patient_name || "-"}</TableCell>
                      <TableCell>{formatDate(order.created_at)}</TableCell>
                      <TableCell>{formatTime(order.created_at)}</TableCell>
                      <TableCell>{order.studies?.length || 0}</TableCell>
                      <TableCell className="font-medium">
                        ${order.total?.toLocaleString() || 0}
                      </TableCell>
                      <TableCell>
                        <Badge variant={
                          order.status === 'completed' ? 'default' :
                          order.status === 'in_progress' ? 'secondary' : 'outline'
                        } className={
                          order.status === 'completed' ? 'bg-green-600' :
                          order.status === 'in_progress' ? 'bg-blue-600' : ''
                        }>
                          {order.status === 'completed' ? 'Completado' :
                           order.status === 'in_progress' ? 'En Proceso' : 'Pendiente'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectOrder(order, index);
                            }}
                            data-testid={`edit-${order.id}`}
                          >
                            <FileEdit className="h-4 w-4 mr-1" />
                            Editar
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={(e) => handleDeleteOrder(order, e)}
                            data-testid={`delete-${order.id}`}
                            title="Eliminar solicitud"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
        )}
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Editar Orden {selectedOrder?.order_number || selectedOrder?.patient_folio}</span>
              <div className="flex flex-wrap gap-2 items-center justify-end">
                {/* Receipt buttons */}
                <div className="flex">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleReceipt('preview')} 
                    title="Ver Recibo"
                    className="rounded-r-none border-r-0"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleReceipt('download')} 
                    title="Descargar Recibo"
                    className="rounded-none border-r-0"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleReceipt('print')} 
                    title="Imprimir Recibo"
                    className="rounded-none border-r-0"
                  >
                    <Printer className="h-4 w-4 mr-1" />
                    <span className="hidden sm:inline">Recibo</span>
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={() => handleReceipt('direct')} 
                    title="Impresión Directa Recibo"
                    className="rounded-l-none bg-amber-500 hover:bg-amber-600 text-white"
                    data-testid="direct-print-receipt-btn"
                  >
                    <Zap className="h-4 w-4" />
                  </Button>
                </div>
                {/* Ticket 58mm (impresora térmica) */}
                <div className="flex">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleTicket58('preview')} 
                    title="Ver Ticket 58mm"
                    className="rounded-r-none border-r-0"
                    data-testid="preview-ticket58-btn"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleTicket58('download')} 
                    title="Descargar Ticket 58mm"
                    className="rounded-none border-r-0"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleTicket58('print')} 
                    title="Imprimir Ticket 58mm"
                    className="rounded-none border-r-0"
                  >
                    <Printer className="h-4 w-4 mr-1" />
                    <span className="hidden sm:inline">58mm</span>
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={() => handleTicket58('direct')} 
                    title="Impresión Directa Ticket 58mm"
                    className="rounded-l-none bg-teal-600 hover:bg-teal-700 text-white"
                    data-testid="direct-print-ticket58-btn"
                  >
                    <Zap className="h-4 w-4" />
                  </Button>
                </div>
                {/* Labels buttons */}
                <div className="flex">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleLabels('preview')} 
                    title="Ver Etiquetas"
                    className="rounded-r-none border-r-0"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleLabels('download')} 
                    title="Descargar Etiquetas"
                    className="rounded-none border-r-0"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleLabels('print')} 
                    title="Imprimir Etiquetas"
                    className="rounded-none border-r-0"
                  >
                    <Printer className="h-4 w-4 mr-1" />
                    <span className="hidden sm:inline">Etiquetas</span>
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={() => handleLabels('direct')} 
                    title="Impresión Directa Etiquetas"
                    className="rounded-l-none bg-amber-500 hover:bg-amber-600 text-white"
                    data-testid="direct-print-labels-btn"
                  >
                    <Zap className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Patient Info Row */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Paciente</Label>
                <Input value={selectedOrder?.patient_name || ""} disabled />
              </div>
              <div className="space-y-2">
                <Label>Empresa</Label>
                <Select 
                  value={editData.company_id || "none"} 
                  onValueChange={handleCompanyChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar empresa..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin empresa (PARTICULAR)</SelectItem>
                    {companies.map(company => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Sucursal</Label>
                <Select 
                  value={editData.branch || "none"} 
                  onValueChange={(val) => setEditData(prev => ({ ...prev, branch: val === "none" ? "" : val }))}
                  data-testid="edit-branch-select"
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar sucursal..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin sucursal</SelectItem>
                    {branches.map((branch) => (
                      <SelectItem key={branch.name || branch} value={branch.name || branch}>
                        {branch.name || branch}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Doctor Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Médico que envía</Label>
                <Popover open={doctorSearchOpen} onOpenChange={setDoctorSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={doctorSearchOpen}
                      className="w-full justify-between font-normal"
                      data-testid="edit-doctor-select"
                    >
                      <span className="truncate">{getSelectedDoctorName()}</span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px] p-0" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder="Buscar médico..."
                        value={doctorSearchTerm}
                        onValueChange={setDoctorSearchTerm}
                        data-testid="edit-doctor-search-input"
                      />
                      <CommandList>
                        <CommandEmpty>No se encontró el médico</CommandEmpty>
                        <CommandGroup className="max-h-60 overflow-y-auto">
                          <CommandItem
                            value="none"
                            onSelect={() => {
                              setEditData(prev => ({ ...prev, doctor_id: "" }));
                              setDoctorSearchOpen(false);
                              setDoctorSearchTerm("");
                            }}
                            className={cn(
                              "cursor-pointer",
                              !editData.doctor_id && "bg-primary text-primary-foreground"
                            )}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                !editData.doctor_id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            A QUIEN CORRESPONDA
                          </CommandItem>
                          {filteredDoctors.map((doctor) => (
                            <CommandItem
                              key={doctor.id}
                              value={doctor.id}
                              onSelect={() => {
                                setEditData(prev => ({ ...prev, doctor_id: doctor.id }));
                                setDoctorSearchOpen(false);
                                setDoctorSearchTerm("");
                              }}
                              className={cn(
                                "cursor-pointer",
                                editData.doctor_id === doctor.id && "bg-primary text-primary-foreground"
                              )}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  editData.doctor_id === doctor.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {doctor.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Fecha</Label>
                <Input value={formatDate(selectedOrder?.created_at)} disabled />
              </div>
            </div>

            {/* Time Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Hora</Label>
                <Input value={formatTime(selectedOrder?.created_at)} disabled />
              </div>
              <div></div>
            </div>

            {/* Studies Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Estudios</Label>
                {activePriceListId && (
                  <span className="text-xs text-muted-foreground">
                    Lista: {priceLists.find(p => p.id === activePriceListId)?.name || "Default"}
                  </span>
                )}
              </div>
              
              {/* Study Search Input */}
              <div className="relative" ref={studySearchRef}>
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar estudio o perfil..."
                  value={studySearchTerm}
                  onChange={(e) => {
                    setStudySearchTerm(e.target.value);
                    setShowStudyDropdown(e.target.value.length > 0);
                    setStudyDropdownIndex(0);
                  }}
                  onFocus={() => {
                    if (studySearchTerm) {
                      setShowStudyDropdown(true);
                      setStudyDropdownIndex(0);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (!showStudyDropdown || combinedSearchResults.length === 0) return;
                    
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setStudyDropdownIndex(prev => 
                        prev < combinedSearchResults.length - 1 ? prev + 1 : 0
                      );
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setStudyDropdownIndex(prev => 
                        prev > 0 ? prev - 1 : combinedSearchResults.length - 1
                      );
                    } else if (e.key === 'Enter') {
                      e.preventDefault();
                      const selectedItem = combinedSearchResults[studyDropdownIndex];
                      if (selectedItem) {
                        if (selectedItem.type === 'profile') {
                          addProfile(selectedItem);
                        } else {
                          addStudy(selectedItem);
                        }
                      }
                    } else if (e.key === 'Escape') {
                      setShowStudyDropdown(false);
                    }
                  }}
                  className="pl-10"
                  data-testid="search-study"
                />
                {studySearchTerm && (
                  <button 
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      setStudySearchTerm("");
                      setShowStudyDropdown(false);
                      setStudyDropdownIndex(0);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
                
                {/* Search Results Dropdown */}
                {showStudyDropdown && combinedSearchResults.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {combinedSearchResults.map((item, index) => {
                      const isProfile = item.type === 'profile';
                      const price = isProfile ? (item.price || 0) : getStudyPrice(item.id);
                      const isSelected = index === studyDropdownIndex;
                      return (
                        <button
                          key={`${item.type}_${item.id}`}
                          className={`w-full px-4 py-2 text-left flex justify-between items-center transition-colors ${
                            isSelected 
                              ? "bg-primary text-primary-foreground" 
                              : isProfile 
                                ? "hover:bg-blue-50 bg-blue-50/50"
                                : "hover:bg-muted"
                          }`}
                          onClick={() => isProfile ? addProfile(item) : addStudy(item)}
                          onMouseEnter={() => setStudyDropdownIndex(index)}
                        >
                          <span className="flex items-center gap-2">
                            {isProfile && <Package className={`h-4 w-4 ${isSelected ? 'text-primary-foreground' : 'text-blue-600'}`} />}
                            {item.code && <span className={`font-mono ${isSelected ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>{item.code}</span>}
                            <span>{item.name}</span>
                            {isProfile && (
                              <span className={`text-xs px-1.5 py-0.5 rounded ${isSelected ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-blue-100 text-blue-700'}`}>
                                Perfil ({item.study_ids?.length || 0} estudios)
                              </span>
                            )}
                          </span>
                          <span className={`font-semibold ${isSelected ? 'text-primary-foreground' : 'text-green-600'}`}>${price}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
                
                {showStudyDropdown && studySearchTerm && combinedSearchResults.length === 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg p-4 text-center text-muted-foreground">
                    No se encontraron estudios ni perfiles
                  </div>
                )}
              </div>
              
              {/* Studies Table */}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Código</TableHead>
                      <TableHead>Estudio</TableHead>
                      <TableHead className="text-right">Precio</TableHead>
                      <TableHead className="w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {editData.studies.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground py-4">
                          No hay estudios agregados
                        </TableCell>
                      </TableRow>
                    ) : (
                      editData.studies.map((study, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-mono">{study.study_code || "-"}</TableCell>
                          <TableCell>{study.study_name}</TableCell>
                          <TableCell className="text-right font-medium">${study.price?.toLocaleString() || 0}</TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => removeStudy(study.study_id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Discount and Notes */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Descuento ($)</Label>
                <Input
                  type="number"
                  value={editData.discount}
                  onChange={(e) => setEditData(prev => ({ 
                    ...prev, 
                    discount: parseFloat(e.target.value) || 0 
                  }))}
                  data-testid="edit-discount"
                />
              </div>
              <div className="space-y-2">
                <Label>Notas</Label>
                <Textarea
                  value={editData.notes}
                  onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={2}
                  data-testid="edit-notes"
                />
              </div>
            </div>

            {/* Totals */}
            <div className="bg-muted/30 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span>Subtotal ({editData.studies.length} estudio{editData.studies.length !== 1 ? 's' : ''}):</span>
                <span>${subtotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Descuento:</span>
                <span>-${(editData.discount || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between font-bold text-lg border-t pt-2">
                <span>Total:</span>
                <span className="text-green-600">${total.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex justify-between items-center pt-4 border-t">
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={goToPrevious} disabled={filteredOrders.length <= 1}>
                <ChevronLeft className="h-4 w-4 mr-1" />
                Anterior
              </Button>
              <Button variant="outline" size="sm" onClick={goToNext} disabled={filteredOrders.length <= 1}>
                Siguiente
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setSelectedOrder(null)}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-edit-btn">
                {saving ? "Guardando..." : "Guardar Cambios"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Registrar Pago
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Summary */}
            <div className="p-4 bg-muted rounded-lg space-y-3">
              <div className="flex justify-between text-sm">
                <span>Total de la orden:</span>
                <span className="font-medium">${paymentData.newTotal?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Ya pagado:</span>
                <span className="font-medium">${paymentData.previousPaid?.toLocaleString()}</span>
              </div>
              {paymentData.difference > 0 && (
                <>
                  <Separator />
                  <div className="flex justify-between text-sm font-bold text-blue-600">
                    <span>Diferencia (estudios agregados):</span>
                    <span>+${paymentData.difference?.toLocaleString()}</span>
                  </div>
                </>
              )}
              <Separator />
              <div className={`flex justify-between text-sm font-bold ${(paymentData.pendingBalance || 0) > 0 ? 'text-orange-600' : 'text-emerald-600'}`}>
                <span>Saldo pendiente:</span>
                <span>${(paymentData.pendingBalance || 0).toLocaleString()}</span>
              </div>
            </div>
            
            {/* New payment amount */}
            <div className="space-y-2">
              <Label>Monto a pagar ($)</Label>
              <Input
                type="number"
                min="0"
                placeholder="0"
                value={paymentData.additionalAmount}
                onChange={(e) => setPaymentData(prev => ({
                  ...prev,
                  additionalAmount: e.target.value
                }))}
                className="text-lg font-bold"
                autoFocus
                data-testid="payment-amount-input"
              />
            </div>
            
            {/* New pending balance */}
            <div className={`p-3 rounded-lg ${calculateNewPendingBalance() > 0 ? 'bg-orange-50 border border-orange-200' : 'bg-emerald-50'}`}>
              <div className="flex justify-between text-sm">
                <span className={calculateNewPendingBalance() > 0 ? 'text-orange-700' : 'text-emerald-700'}>
                  Saldo después del pago:
                </span>
                <span className={`font-bold ${calculateNewPendingBalance() > 0 ? 'text-orange-700' : 'text-emerald-700'}`}>
                  ${calculateNewPendingBalance().toLocaleString()}
                </span>
              </div>
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={async () => {
              setShowPaymentDialog(false);
              // Save without payment
              if (pendingOrderUpdate) {
                setSaving(true);
                try {
                  const previousPaid = selectedOrder.amount_paid || 0;
                  const newPendingBalance = Math.max(0, paymentData.newTotal - previousPaid);
                  await workOrdersApi.update(selectedOrder.id, {
                    ...pendingOrderUpdate,
                    pending_balance: newPendingBalance
                  });
                  toast.success("Solicitud guardada (sin pago)");
                  fetchOrders();
                } catch (error) {
                  toast.error("Error al guardar");
                } finally {
                  setSaving(false);
                  setPendingOrderUpdate(null);
                }
              }
            }}
            data-testid="save-without-payment-btn"
            >
              Guardar sin pagar
            </Button>
            <Button onClick={handlePaymentConfirm} disabled={saving} data-testid="confirm-payment-btn">
              {saving ? "Guardando..." : "Confirmar Pago"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt Preview Dialog - Compatible con Android */}
      <Dialog open={showReceiptPreview} onOpenChange={setShowReceiptPreview}>
        <DialogContent className="max-w-4xl h-[90vh] sm:h-[85vh] p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Recibo de Pago
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 h-full overflow-hidden px-2 pb-2">
            {receiptUrl && (
              <MobilePDFViewer 
                pdfUrl={receiptUrl}
                title="Recibo de Pago"
                filename={`recibo-${selectedOrder?.order_number || selectedOrder?.patient_folio || 'orden'}.pdf`}
                onPrint={() => setShowReceiptPreview(false)}
                onDirectPrint={(url) => printRecibo(url)}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Ticket 58mm Preview Dialog */}
      <Dialog open={showTicketPreview} onOpenChange={setShowTicketPreview}>
        <DialogContent className="max-w-4xl h-[90vh] sm:h-[85vh] p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Ticket 58mm (Impresora Térmica)
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 h-full overflow-hidden px-2 pb-2">
            {ticketUrl && (
              <MobilePDFViewer 
                pdfUrl={ticketUrl}
                title="Ticket 58mm"
                filename={`ticket-${selectedOrder?.order_number || selectedOrder?.patient_folio || 'orden'}.pdf`}
                onPrint={() => setShowTicketPreview(false)}
                initialScale={2.0}
                onDirectPrint={(url) => printTicket58(url)}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={showLabelPreview} onOpenChange={setShowLabelPreview}>
        <DialogContent className="max-w-4xl h-[90vh] sm:h-[85vh] p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" />
              Etiquetas de Muestras
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 h-full overflow-hidden px-2 pb-2">
            {labelUrl && (
              <MobilePDFViewer 
                pdfUrl={labelUrl}
                title="Etiquetas de Muestras"
                filename={`etiquetas-${selectedOrder?.order_number || selectedOrder?.patient_folio || 'orden'}.pdf`}
                onPrint={() => setShowLabelPreview(false)}
                isLabel={true}
                onDirectPrint={(url) => printEtiquetas(url)}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
